import { useEffect, useState } from "react";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import {
  approveOutreach,
  listOutreach,
  rejectOutreach,
  requestOutreachApproval,
  type OutreachDraft,
} from "../api/marketing.ts";

const STATUS_COLOR: Record<string, string> = {
  DRAFT: "bg-gray-700/40 text-gray-300",
  PENDING_APPROVAL: "bg-yellow-900/40 text-yellow-300",
  APPROVED: "bg-green-900/40 text-green-300",
  REJECTED: "bg-red-900/40 text-red-400",
  SENT: "bg-purple-900/40 text-purple-300",
};

export function MarketingOutreachPage() {
  const { token } = useAuth();
  const [items, setItems] = useState<OutreachDraft[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [acting, setActing] = useState<string | null>(null);

  const load = () => {
    if (token == null) return;
    setLoading(true);
    listOutreach(token, { status: statusFilter || undefined, limit: 50 })
      .then((r) => { setItems(r.items); setTotal(r.total); })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [token, statusFilter]);

  async function act(item: OutreachDraft, action: "submit" | "approve" | "reject") {
    if (token == null) return;
    setActing(item.id);
    try {
      if (action === "submit") await requestOutreachApproval(token, item.id, item.version);
      else if (action === "approve") await approveOutreach(token, item.id, item.version);
      else await rejectOutreach(token, item.id, item.version);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed");
    } finally {
      setActing(null);
    }
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Outreach Drafts ({total.toLocaleString()})</h1>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm bg-gray-900 border border-gray-700 rounded px-2 py-1 text-gray-300">
            <option value="">All Statuses</option>
            {["DRAFT", "PENDING_APPROVAL", "APPROVED", "REJECTED", "SENT"].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        <div className="rounded-lg border border-amber-900/30 bg-amber-900/10 p-4 text-sm text-amber-300">
          Outreach drafts must be <strong>approved by a human</strong> before sending. Approved drafts must be sent manually through your email client.
        </div>

        {error != null && <p className="text-red-400 text-sm">{error}</p>}
        {loading && <p className="text-gray-400 text-sm">Loading…</p>}

        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.id} className="rounded-lg border border-gray-800 p-4 space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-0.5">
                  <p className="font-medium text-white">{item.subject}</p>
                  <p className="text-xs text-gray-500">
                    To: {item.toName != null ? `${item.toName} ` : ""}{item.toEmail ?? "—"}
                    {item.generatedBy != null && ` · AI: ${item.generatedBy}`}
                  </p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded shrink-0 ${STATUS_COLOR[item.status] ?? "bg-gray-700 text-gray-400"}`}>{item.status}</span>
              </div>

              <p className="text-sm text-gray-400 line-clamp-3">{item.body}</p>

              <div className="flex gap-2">
                {item.status === "DRAFT" && (
                  <button disabled={acting === item.id} onClick={() => void act(item, "submit")}
                    className="text-xs px-3 py-1 rounded bg-blue-900/50 text-blue-300 hover:bg-blue-800/50 disabled:opacity-50">
                    Submit for Approval
                  </button>
                )}
                {item.status === "PENDING_APPROVAL" && (
                  <>
                    <button disabled={acting === item.id} onClick={() => void act(item, "approve")}
                      className="text-xs px-3 py-1 rounded bg-green-900/50 text-green-300 hover:bg-green-800/50 disabled:opacity-50">
                      Approve
                    </button>
                    <button disabled={acting === item.id} onClick={() => void act(item, "reject")}
                      className="text-xs px-3 py-1 rounded bg-red-900/50 text-red-400 hover:bg-red-800/50 disabled:opacity-50">
                      Reject
                    </button>
                  </>
                )}
                {item.status === "APPROVED" && (
                  <p className="text-xs text-green-400">Ready — send manually via your email client.</p>
                )}
              </div>
            </div>
          ))}
          {items.length === 0 && !loading && (
            <p className="text-center text-gray-500 py-8">No outreach drafts found.</p>
          )}
        </div>
      </div>
    </Layout>
  );
}
